import React, { useState, useEffect } from 'react';
import { X, Calendar, User, AlertCircle, Loader2 } from 'lucide-react';

const CreateTaskModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  leadId, 
  teamMembers = [],
  leads = [],
  initialData = null,
  initialDate = null,
  isEditing = false
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    dueTime: '',
    priority: 'medium',
    assignedTo: '',
    status: 'pending',
    selectedLeadId: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      if (initialData && isEditing) {
        // Parse due_date if it exists
        let dueDate = '';
        let dueTime = '';
        if (initialData.due_date) {
          const date = new Date(initialData.due_date);
          dueDate = date.toISOString().split('T')[0];
          dueTime = date.toTimeString().split(' ')[0].slice(0, 5);
        }
        
        setFormData({
          title: initialData.title || '',
          description: initialData.description || '',
          dueDate: dueDate,
          dueTime: dueTime,
          priority: initialData.priority || 'medium',
          assignedTo: initialData.assigned_to || '',
          status: initialData.status || 'pending',
          selectedLeadId: initialData.lead_id || leadId || ''
        });
      } else {
        // Reset form for new task
        // If initialDate is provided, use it for dueDate
        let dueDate = '';
        if (initialDate) {
          const date = new Date(initialDate);
          dueDate = date.toISOString().split('T')[0];
        }
        
        setFormData({
          title: '',
          description: '',
          dueDate: dueDate,
          dueTime: '09:00', // Default to 9:00 AM
          priority: 'medium',
          assignedTo: '',
          status: 'pending',
          selectedLeadId: leadId || ''
        });
      }
      setErrors({});
      setLoading(false);
    }
  }, [isOpen, initialData, isEditing, initialDate, leadId]);
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Task title is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Prevent multiple submissions
    if (loading) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Combine date and time if both are provided
      let dueDate = null;
      if (formData.dueDate) {
        // Use provided time or default to 9:00 AM
        const timeToUse = formData.dueTime || '09:00';
        dueDate = `${formData.dueDate}T${timeToUse}:00`;
      }
      
      await onSubmit({
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        dueDate: dueDate,
        priority: formData.priority,
        assignedTo: formData.assignedTo || null,
        status: formData.status,
        leadId: formData.selectedLeadId || null
      });
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-[var(--sf-border-light)] flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-[var(--sf-text-primary)]">
            {isEditing ? 'Edit Task' : 'Create New Task'}
          </h2>
          <button
            onClick={onClose}
            className="text-[var(--sf-text-muted)] hover:text-[var(--sf-text-secondary)]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-4 sm:p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                Task Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)] ${
                  errors.title ? 'border-red-300' : 'border-[var(--sf-border-light)]'
                }`}
                placeholder="e.g., Follow up on Monday"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.title}
                </p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)]"
                placeholder="Add task details..."
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                  Due Time
                </label>
                <input
                  type="time"
                  value={formData.dueTime}
                  onChange={(e) => setFormData({ ...formData, dueTime: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)]"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)]"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              
              {isEditing && (
                <div>
                  <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)]"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              )}
            </div>
            
            {!isEditing && (
              <div>
                <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                  Attach to Lead {leads.length === 0 ? '' : '*'}
                </label>
                <select
                  value={formData.selectedLeadId}
                  onChange={(e) => setFormData({ ...formData, selectedLeadId: e.target.value })}
                  disabled={leads.length === 0}
                  className="w-full px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)] disabled:bg-gray-50 disabled:text-[var(--sf-text-muted)]"
                >
                  {leads.length === 0 ? (
                    <option value="">No leads available yet</option>
                  ) : (
                    <>
                      <option value="">Pick a lead…</option>
                      {leads.map((lead) => (
                        <option key={lead.id} value={lead.id}>
                          {lead.first_name} {lead.last_name} {lead.company ? `- ${lead.company}` : ''}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                <p className="mt-1 text-xs text-[var(--sf-text-muted)]">
                  Tasks are grouped by lead today. Create a lead first (Opportunities &rarr; New) if you need to attach a task but have none yet.
                </p>
              </div>
            )}
            
            {teamMembers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-[var(--sf-text-primary)] mb-1">
                  Assign To
                </label>
                <select
                  value={formData.assignedTo}
                  onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--sf-border-light)] rounded-lg focus:ring-2 focus:ring-[var(--sf-blue-500)] focus:border-[var(--sf-blue-500)]"
                >
                  <option value="">Unassigned</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-6 mt-6 border-t border-[var(--sf-border-light)]">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 text-[var(--sf-text-secondary)] bg-white border border-[var(--sf-border-light)] rounded-lg hover:bg-[var(--sf-bg-hover)] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto px-4 py-2 bg-[var(--sf-blue-500)] text-white rounded-lg hover:bg-[var(--sf-blue-600)] font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditing ? 'Update Task' : 'Create Task'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTaskModal;

